import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import type { TenantThemeConfig } from '../types/theme'
import { useAuth } from '../contexts/AuthContext'
import { getTenantIdFromConfig } from '../utils/tenantUtils'
import { getThemeAssetUrl } from '../utils/themeUtils'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface LoginPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

const LoginPage: React.FC<LoginPageProps> = ({ themeConfig, assetConfig }) => {
  const navigate = useNavigate()
  const { login, isAuthenticated, isLoading, user } = useAuth()
  
  // Get theme-specific assets path
  const getAssetUrl = (path: string) => {
    return getThemeAssetUrl(assetConfig.CDN_BASE_URL, themeConfig.themeId, path)
  }
  
  // Extract configuration from themeConfig
  const { branding } = themeConfig.theme
  
  // Get tenant ID using utility function
  const getTenantId = (): string => {
    return getTenantIdFromConfig(themeConfig, user)
  }
  
  // Login form state — โหลด savedUsername และ rememberMe จาก localStorage (ให้ตรงกับ ClassicLayout)
  const [formData, setFormData] = useState(() => {
    if (typeof localStorage === 'undefined') {
      return { username: '', password: '', rememberMe: true }
    }
    const saved = localStorage.getItem('savedUsername') || ''
    const remember = localStorage.getItem('rememberMe') !== 'false' || !!saved
    return {
      username: saved,
      password: '',
      rememberMe: remember
    }
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // Redirect to dashboard when authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, isLoading, navigate])
  
  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }
  
  // Perform login logic
  const performLogin = async () => {
    
    // Validate inputs
    if (!formData.username.trim()) {
      await Swal.fire({
        icon: 'warning',
        title: 'ข้อมูลไม่ครบถ้วน',
        text: 'กรุณากรอกชื่อผู้ใช้หรือเบอร์โทรศัพท์',
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#ffc107'
      })
      return
    }

    if (!formData.password.trim()) {
      await Swal.fire({
        icon: 'warning',
        title: 'ข้อมูลไม่ครบถ้วน',
        text: 'กรุณากรอกรหัสผ่าน',
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#ffc107'
      })
      return
    }
    
    setIsSubmitting(true)
    
    try {
      console.log('🔐 Attempting login:', {
        username: formData.username,
        rememberMe: formData.rememberMe
      })
      
      // Use AuthContext login method to ensure state is updated
      const result = await login(formData.username, formData.password, formData.rememberMe, getTenantId())
      
      if (result.success) {
        // Get user data from localStorage (set by authService)
        const userData = JSON.parse(localStorage.getItem('user') || '{}')
        const displayName = userData.fullName || userData.displayName || userData.username || formData.username
        
        await Swal.fire({
          icon: 'success',
          title: 'เข้าสู่ระบบสำเร็จ',
          text: `ยินดีต้อนรับ ${displayName}`,
          confirmButtonText: 'ตกลง',
          confirmButtonColor: '#28a745',
          timer: 1500,
          timerProgressBar: true
        })
        
        // Navigation will happen automatically via useEffect when isAuthenticated becomes true
        // But we can also navigate immediately as fallback
        setTimeout(() => {
          navigate('/dashboard', { replace: true })
        }, 100)
      } else {
        // Handle concurrent login error
        if (result.errorMessage === 'CONCURRENT_LOGIN_BLOCKED') {
          // Show loading message
          Swal.fire({
            title: 'กำลังออกจากระบบจากอุปกรณ์อื่น...',
            text: 'กรุณารอสักครู่',
            allowOutsideClick: false,
            showConfirmButton: false,
            didOpen: () => {
              Swal.showLoading()
            }
          })
          
          // Get tenant ID dynamically
          const tenantId = getTenantId()
          
          // Import authService for force logout
          const { authService } = await import('../services/authService')
          await authService.forceLogoutFromAllDevices(tenantId, formData.username)
          
          // Wait a moment for logout to complete
          await new Promise(resolve => setTimeout(resolve, 1000))
          
          // Close loading and show success
          Swal.fire({
            icon: 'success',
            title: 'ออกจากระบบสำเร็จ',
            text: 'กำลังเข้าสู่ระบบใหม่...',
            timer: 1500,
            showConfirmButton: false
          })
          
          // Retry login automatically
          setTimeout(() => {
            performLogin()
          }, 1500)
          
          return // Exit early
        }
        
        await Swal.fire({
          icon: 'error',
          title: 'เข้าสู่ระบบไม่สำเร็จ',
          text: result.errorMessage || 'ไม่สามารถเข้าสู่ระบบได้',
          confirmButtonText: 'ตกลง',
          confirmButtonColor: '#dc3545'
        })
      }
      
    } catch (error: any) {
      console.error('❌ Login error:', error)
      
      let errorMessage = 'ไม่สามารถเข้าสู่ระบบได้ กรุณาลองใหม่อีกครั้ง'
      
      if (error.status === 401) {
        errorMessage = 'ชื่อผู้ใช้งานหรือรหัสผ่านไม่ถูกต้อง'
      } else if (error.status === 423) {
        errorMessage = 'บัญชีถูกระงับชั่วคราว เนื่องจากพยายามเข้าสู่ระบบผิดหลายครั้ง'
      } else if (error.status === 403) {
        errorMessage = 'บัญชีไม่สามารถใช้งานได้'
      } else if (error.message) {
        errorMessage = error.message
      }
      
      // Show error message
      await Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: errorMessage,
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#dc3545'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await performLogin()
  }

  return (
    <div className="content py-3">
      <div className="container">
        <div className="row mx--1">
          <div className="col-12 col-lg-6 offset-lg-3 col-xl-4 offset-xl-4">
            <div className="box-login text-center bgi-2 bdr-10 c-white bx-sd p-2 mb-3">
              <div className="bgc-16 bdr-10 p-3 p-lg-4">
                <div className="box-regis w-100">
                  <div className="img-regis mx-auto">
                    <img 
                      src={getAssetUrl('/img/logo-m.png')} 
                      alt={branding.siteName}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = getAssetUrl('/img/logo-m.png')
                      }}
                    />
                  </div>
                </div>

                <h3 className="txt-user mb-1 text-center t-medium">สมาชิก</h3>

                <div className="wrap justify-content-center mb-3">
                  <h6 className="text-center">ยินดีต้อนรับสมาชิก</h6>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="form-inp bdr-10">
                    <div className="icon ps-1">
                      <i className="fas fa-user"></i>
                    </div>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      placeholder="ชื่อผู้ใช้ หรือ เบอร์โทรศัพท์"
                      autoComplete="username"
                      disabled={isSubmitting}
                      required
                      style={{ color: '#0f172a' }}
                    />
                  </div>

                  <div className="form-inp bdr-10">
                    <div className="icon ps-1">
                      <i className="fas fa-key"></i>
                    </div>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="รหัสผ่าน"
                      autoComplete="current-password"
                      disabled={isSubmitting}
                      required
                      style={{ color: '#0f172a' }}
                    />
                  </div>

                  <div className="wrap justify-content-between my-2">
                    <div className="checkboxes">
                      <div className="checkboxes__row">
                        <div className="checkboxes__item">
                          <label className="checkbox style-a">
                            <input
                              type="checkbox"
                              name="rememberMe"
                              checked={formData.rememberMe}
                              onChange={handleInputChange}
                              disabled={isSubmitting}
                            />
                            <div className="checkbox__checkmark"></div>
                            <div className="checkbox__body c-white">
                              จำฉันไว้ในระบบ
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                    <Link to="/forgot-password" className="txt-link c-white">
                      <i className="fas fa-key me-1"></i>
                      ลืมรหัสผ่าน
                    </Link>
                  </div>

                  <div className="row mx--1 mt-2">
                    <div className="col-12 mb-2 px-1">
                      <button
                        type="submit"
                        className="btn_main c-black wrap justify-content-center t-medium bgg-2 c-red bdr-10 w-100"
                        disabled={isSubmitting}
                        style={{ border: 'none' }}
                      >
                        {isSubmitting ? (
                          <>
                            <div className="spinner-border spinner-border-sm me-2" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            กำลังเข้าสู่ระบบ...
                          </>
                        ) : (
                          'เข้าสู่ระบบ'
                        )}
                      </button>
                    </div>
                    <div className="col-12 px-1">
                      <Link
                        to="/register"
                        className="btn_main c-black wrap justify-content-center t-medium bgg-1 c-white bdr-10"
                        style={{ textDecoration: 'none' }}
                      >
                        สมัครสมาชิก
                      </Link>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
